import React, { useCallback, useEffect, useState } from 'react';
import { Button, DatePicker, Input, Select, Space, Table, Tag } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { getRebates } from '../../api/rebates';

const statusMap = {
  PENDING: { label: '待确认', color: 'orange' },
  CONFIRMED: { label: '已确认', color: 'blue' },
  SETTLED: { label: '已结算', color: 'green' },
  REFUNDED: { label: '已退款', color: 'red' },
};

const platformMap = {
  TAOBAO: { label: '淘宝', color: 'orange' },
  JD: { label: '京东', color: 'red' },
};

const Rebates = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialFilters = {
    status: searchParams.get('status') || '',
    platform: searchParams.get('platform') || '',
    userId: searchParams.get('userId') || '',
    orderTimeFrom: searchParams.get('orderTimeFrom') || '',
    orderTimeTo: searchParams.get('orderTimeTo') || '',
  };

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ list: [], summary: null });
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [filters, setFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);
  const currentPage = pagination.current;
  const currentPageSize = pagination.pageSize;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getRebates({
        page: currentPage,
        size: currentPageSize,
        status: appliedFilters.status || undefined,
        platform: appliedFilters.platform || undefined,
        userId: appliedFilters.userId || undefined,
        orderTimeFrom: appliedFilters.orderTimeFrom || undefined,
        orderTimeTo: appliedFilters.orderTimeTo || undefined,
      });
      setData(result);
      setPagination((prev) => ({ ...prev, total: result.total }));
    } catch (error) {
      console.error('Failed to fetch rebates:', error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, currentPageSize, appliedFilters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearch = () => {
    const nextFilters = { ...filters };
    setAppliedFilters(nextFilters);
    setPagination((prev) => ({ ...prev, current: 1 }));

    const params = {};
    if (nextFilters.userId) {
      params.userId = nextFilters.userId;
    }
    if (nextFilters.status) {
      params.status = nextFilters.status;
    }
    if (nextFilters.platform) {
      params.platform = nextFilters.platform;
    }
    if (nextFilters.orderTimeFrom) {
      params.orderTimeFrom = nextFilters.orderTimeFrom;
    }
    if (nextFilters.orderTimeTo) {
      params.orderTimeTo = nextFilters.orderTimeTo;
    }
    setSearchParams(params);
  };

  const dateRange = filters.orderTimeFrom && filters.orderTimeTo
    ? [dayjs(filters.orderTimeFrom), dayjs(filters.orderTimeTo)]
    : null;
  const columns = [
    { title: 'ID', dataIndex: 'id', width: 80 },
    {
      title: '用户ID',
      dataIndex: 'userId',
      width: 120,
      render: (value) => (
        <Button type="link" style={{ padding: 0 }} onClick={() => navigate(`/users?userId=${value}`)}>
          {value}
        </Button>
      ),
    },
    { title: '订单号', dataIndex: 'orderId', ellipsis: true },
    {
      title: '平台',
      dataIndex: 'platform',
      render: (platform) => {
        const item = platformMap[platform] || { label: platform, color: 'default' };
        return <Tag color={item.color}>{item.label}</Tag>;
      },
    },
    {
      title: '商品',
      dataIndex: 'productTitle',
      ellipsis: true,
      width: 200,
    },
    {
      title: '订单金额',
      dataIndex: 'orderAmount',
      render: (value) => `¥${Number(value || 0).toFixed(2)}`,
    },
    {
      title: '佣金',
      dataIndex: 'commissionAmount',
      render: (value) => `¥${Number(value || 0).toFixed(2)}`,
    },
    {
      title: '返利金额',
      dataIndex: 'actualRebateAmount',
      render: (value) => `¥${Number(value || 0).toFixed(2)}`,
    },
    {
      title: '返利类型',
      dataIndex: 'rebateType',
      render: (type) => (
        <Tag color={type === 'INSTANT' ? 'green' : 'blue'}>
          {type === 'INSTANT' ? '秒返' : '延迟'}
        </Tag>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      render: (status) => {
        const item = statusMap[status] || { label: status, color: 'default' };
        return <Tag color={item.color}>{item.label}</Tag>;
      },
    },
    {
      title: '下单时间',
      dataIndex: 'orderTime',
      render: (value) => (value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '-'),
      width: 140,
    },
  ];

  return (
    <div>
      <h2>返利记录</h2>

      <Space style={{ marginBottom: 16 }} wrap>
        <Input
          placeholder="用户ID"
          value={filters.userId}
          onChange={(e) => setFilters((prev) => ({ ...prev, userId: e.target.value }))}
          onPressEnter={handleSearch}
          style={{ width: 140 }}
        />
        <Select
          placeholder="状态"
          allowClear
          style={{ width: 120 }}
          value={filters.status || undefined}
          onChange={(value) => setFilters((prev) => ({ ...prev, status: value || '' }))}
          options={[
            { value: 'PENDING', label: '待确认' },
            { value: 'CONFIRMED', label: '已确认' },
            { value: 'SETTLED', label: '已结算' },
            { value: 'REFUNDED', label: '已退款' },
          ]}
        />
        <Select
          placeholder="平台"
          allowClear
          style={{ width: 120 }}
          value={filters.platform || undefined}
          onChange={(value) => setFilters((prev) => ({ ...prev, platform: value || '' }))}
          options={[
            { value: 'TAOBAO', label: '淘宝' },
            { value: 'JD', label: '京东' },
          ]}
        />
        <DatePicker.RangePicker
          value={dateRange}
          format="YYYY-MM-DD"
          placeholder={['开始日期', '结束日期']}
          onChange={(dates, dateStrings) => setFilters((prev) => ({
            ...prev,
            orderTimeFrom: dateStrings?.[0] || '',
            orderTimeTo: dateStrings?.[1] || '',
          }))}
        />
        <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
          搜索
        </Button>
      </Space>

      <Table
        columns={columns}
        dataSource={data.list || []}
        rowKey="id"
        loading={loading}
        scroll={{ x: 1400 }}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showTotal: (total) => `共 ${total} 条`,
          onChange: (page, pageSize) => setPagination({ current: page, pageSize, total: pagination.total }),
        }}
      />
    </div>
  );
};

export default Rebates;
